/**
 * The sign-in screen, in two modes. `pages/login/index.tsx` renders it in `"login"` mode and
 * `pages/easy-quote/index.tsx` in `"easyQuote"` mode; nothing else imports it.
 *
 * - `"login"`: `LoginHelpers/LoginForm.tsx` and `CreateAccountForm.tsx`. On desktop they sit side
 *   by side on a white and dark-gray split; below `theme.breakpoints.lg` (measured with
 *   `useWindowSize`, not `Media`) they stack.
 * - `"easyQuote"`: `EasyQuoteHelpers/EasyQuote.Components.tsx` → `EasyQuoteSteps`, which shows
 *   one of three steps (`EasyQuote.types.ts` → `DisplaySteps`): the launcher, the guest form
 *   (`EasyQuoteGuestForm.tsx`) or the sign-in form in its Easy Quote layout.
 *
 * The current step lives in React state and in sessionStorage `displayStep`. It is written on
 * every change and removed on `beforeunload`, on `pagehide` and on unmount, so it lasts only while
 * the visitor stays on the page. Within that time it carries the step across a remount, which
 * happens on the Easy Quote page: `pages/easy-quote/index.tsx` passes `PublicWithFeature` an
 * inline `component`, a new component type on each render of the page, so React remounts this
 * component whenever the page re-renders. `pages/login/index.tsx` and `LoginForm.tsx` (after a
 * successful sign-in) also remove the key.
 *
 * `redirectSuccessMessage` in the `usePageParams()` state is shown above the sign-in form. Only
 * `components/Register/VerifyToken/RegisterVerifyTokenRoot.tsx` sets it, when the email being
 * registered already has an account.
 *
 * It renders nothing until mounted (`components/Hooks/useHasMounted.ts`), and it reads
 * `sessionStorage` in its state initializer, which is safe because pages render only in the
 * browser (`components/NextLayouts/RootLayout.tsx` → `HasMounted`).
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection, Flex, SplitConfig } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import LoginForm from "./LoginHelpers/LoginForm";
import CreateAccountForm from "./CreateAccountForm";
import { EasyQuoteGuestForm } from "./EasyQuoteHelpers/EasyQuoteGuestForm";
import { useHasMounted } from "@components/Hooks";
import { ForgotPasswordLocationState } from "pages/login/forgot-password";
import { usePageParams } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import { EasyQuoteSteps } from "./EasyQuoteHelpers/EasyQuote.Components";
import { DisplaySteps, LoginMode } from "./EasyQuoteHelpers/EasyQuote.types";
import { useWindowSize } from "@ryerson/frontend.common";

/** Props of {@link Login}. */
export interface LoginProps {
	// `"login"` (default) for `/login`; `"easyQuote"` for `/easy-quote`.
	mode?: LoginMode;
}

/** The sign-in screen for `/login` or `/easy-quote`; see the file header. */
const Login: React.FC<LoginProps> = ({ mode = "login" }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	// const [hasMounted, setHasMounted] = React.useState(false);
	const [displayStep, setDisplayStep] = React.useState<DisplaySteps>(
		(sessionStorage.getItem("displayStep") as DisplaySteps) || ("launcher" as DisplaySteps)
	);

	React.useEffect(() => {
		sessionStorage.setItem("displayStep", displayStep);
	}, [displayStep]);

	React.useEffect(() => {
		const clearDisplayStep = () => {
			sessionStorage.removeItem("displayStep");
		};

		window.addEventListener("beforeunload", clearDisplayStep);
		window.addEventListener("pagehide", clearDisplayStep);

		return () => {
			window.removeEventListener("beforeunload", clearDisplayStep);
			window.removeEventListener("pagehide", clearDisplayStep);
			clearDisplayStep();
		};
	}, []);

	// const content = EasyQuoteContent[language] || EasyQuoteContent.en;
	// give state a default value in case it is undefined to allow destructuring
	const { state: { redirectSuccessMessage: successMessage = "" } = {} } = usePageParams() as {
		state: ForgotPasswordLocationState;
	};
	const isEasyQuote = mode === "easyQuote";

	let windowSize = useWindowSize()[0];
	let isMobile = windowSize < parseInt(theme.breakpoints.lg);

	if (!useHasMounted()) return null;

	const loginSection: SplitConfig = {
		splitDirection: "horizontal",
		splitColors: [
			{
				color: theme.colors.primary.white,
				percentage: "50%",
			},
			{
				color: theme.colors.primary.darkGray,
				percentage: "50%",
			},
		],
	};
	const createAccountSection: SplitConfig = {
		splitDirection: "vertical",
		splitColors: [
			{
				color: theme.colors.primary.darkGray,
				percentage: "100%",
			},
		],
	};

	const RenderLoginForms = (isMobile: boolean) => {
		return (
			<ContentSection type={isEasyQuote ? "tertiary" : "primary"} vPadding="0" hPadding="0">
				{isEasyQuote ? (
					<EasyQuoteSteps
						displayStep={displayStep}
						setDisplayStep={setDisplayStep}
						isMobile={isMobile}
						successMessage={successMessage}
					/>
				) : (
					<>
						{isMobile ? (
							<>
								<ContentSection>
									<LoginForm isMobile={isMobile} message={successMessage} />
								</ContentSection>
								<ContentSection type="split" split={createAccountSection}>
									{/* Always `CreateAccountForm`: this branch is inside the
									non-Easy-Quote case, so `isEasyQuote` is false here. */}
									{isEasyQuote ? (
										<EasyQuoteGuestForm
											isMobile={isMobile}
											setDisplayStep={setDisplayStep}
										/>
									) : (
										<CreateAccountForm isMobile={isMobile} />
									)}
								</ContentSection>
							</>
						) : (
							<ContentSection
								type="split"
								split={loginSection}
								vPadding="0"
								hPadding="0"
							>
								<Flex justifyContent="space-evenly">
									<LoginForm isMobile={isMobile} message={successMessage} />
									<CreateAccountForm isMobile={isMobile} />
								</Flex>
							</ContentSection>
						)}
					</>
				)}
			</ContentSection>
		);
	};
	return <React.Fragment>{RenderLoginForms(isMobile)}</React.Fragment>;
};

export default Login;
